/**
 * @author: Pawan Singh Sisodiya
 * UI refresh: exam summary card, marks-entry grid with live %, grade and pass/fail,
 * absent handling, keyboard entry, progress + class statistics, report-card link.
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import "../../App.css";
import { Badge, Button, Card, Col, Container, Form, InputGroup, ProgressBar, Row, Table } from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import moment from "moment";
import { Helmet } from "react-helmet";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Confirm from "../Confirm";
import schoolApi from "../../api/schoolApi";
import PageNavigations from "../breadcrumbs/PageNavigations";
import { usePermissions } from "../contexts/PermissionContext";
import { SessionContext } from "../SessionProvider";
import reportCardApi from "../reportCard/reportCardApi";

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const to12h = (t) => (t ? moment(t, ["HH:mm:ss", "HH:mm"]).format("hh:mm A") : "—");

const ExamScheduleView = (props) => {
  const { rolePermissions, userInfo } = usePermissions();
  const { id: scheduleId } = useParams();
  const navigate = useNavigate();
  const { selectedSessionId, currentSessionId } = useContext(SessionContext);

  const [record, setRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [grades, setGrades] = useState([]);
  const inputs = useRef({});

  const isAdmin = userInfo?.userrole === "SYS_ADMIN" || userInfo?.userrole === "ADMIN";
  const canEdit = isAdmin || rolePermissions?.Exam_Schedule?.can_edit;
  const canDelete = isAdmin || rolePermissions?.Exam_Schedule?.can_delete;
  const isCurrentSession = !currentSessionId || selectedSessionId === currentSessionId;

  const maxMarks = Number(record?.max_marks) || 0;
  // GRADE type papers (e.g. Notebook graded A/B/C): a grade is entered instead of marks
  const isGradeType = record?.exam_type_marking === "GRADE";
  const minMarks = Number(record?.min_marks) || 0;
  const examDay = record?.schedule_date ? new Date(new Date(record.schedule_date).toDateString()) : null;
  // marks can be entered / corrected any time in the current session (before or after the exam date)
  const examNotHeld = false;
  void examDay;
  const canEnter = canEdit && isCurrentSession;
  const dirty = rows.some((r) => r.isUpdate);

  /* ---------------- load ---------------- */
  useEffect(() => {
    (async () => {
      try {
        const result = await schoolApi.fetchExamSchedulesById(scheduleId);
        if (result?.success) setRecord(result.examSchedule);
        else toast.error("Exam not found");
      } catch (e) {
        toast.error("Could not load exam");
      }
    })();
    // eslint-disable-next-line
  }, [scheduleId]);

  const loadStudents = async (rec = record) => {
    if (!rec) return;
    try {
      const result = await schoolApi.fetchStudentsByIds(rec.class_id, scheduleId, rec.section_id, rec.sessionid);
      const list = (result?.success ? result.result : []) || [];
      setRows(list.map((r) => ({
        ...r,
        ispresent: r.ispresent == null ? true : r.ispresent,
        obtained_marks: r.obtained_marks == null ? "" : String(Number(r.obtained_marks)),
        obtained_grade: r.obtained_grade || "",
        exam_schedule_id: scheduleId,
        isUpdate: false,
      })));
    } catch (e) {
      setRows([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!record) return;
    loadStudents(record);
    // grade scale (optional – from Report Card Settings)
    reportCardApi.gradeOptions(record.class_id, "SCHOLASTIC").then(setGrades).catch(() => setGrades([]));
    // eslint-disable-next-line
  }, [record]);

  /* ---------------- helpers ---------------- */
  const gradeFor = (pct) => {
    if (pct == null || !grades.length) return "";
    const g = [...grades].sort((a, b) => b.from - a.from).find((x) => pct >= x.from);
    return g ? g.grade : "";
  };

  const rowState = (r) => {
    if (!r.ispresent) return { status: "absent" };
    if (isGradeType) return isBlank(r.obtained_grade) ? { status: "pending" } : { status: "graded", grade: r.obtained_grade };
    if (isBlank(r.obtained_marks)) return { status: "pending" };
    const m = Number(r.obtained_marks);
    if (m > maxMarks) return { status: "invalid" };
    const pct = maxMarks ? Math.round((m * 10000) / maxMarks) / 100 : null;
    return { status: m >= minMarks ? "pass" : "fail", marks: m, pct, grade: gradeFor(pct) };
  };

  const setRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.student_admissionid === id ? { ...r, ...patch, isUpdate: true } : r)));

  const onMarks = (r, value) => {
    if (isGradeType) {
      const g = String(value).toUpperCase().replace(/[^A-Z0-9+\-]/g, "").slice(0, 10);
      return setRow(r.student_admissionid, { obtained_grade: g });
    }
    if (!/^\d*$/.test(value)) return; // whole numbers only (same as before)
    setRow(r.student_admissionid, { obtained_marks: value });
  };

  const onPresent = (r, present) =>
    setRow(r.student_admissionid, { ispresent: present, obtained_marks: present ? (r.ispresent ? r.obtained_marks : "") : "0" });

  // Enter / arrow keys move to the next student
  const onKey = (e, index) => {
    if (!isGradeType && ["-", "+", "e", "E", "."].includes(e.key)) return e.preventDefault();
    const go = (d) => {
      const list = filtered;
      for (let i = index + d; i >= 0 && i < list.length; i += d) {
        const el = inputs.current[list[i].student_admissionid];
        if (el && !el.disabled) { el.focus(); el.select(); break; }
      }
    };
    if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); go(1); }
    if (e.key === "ArrowUp") { e.preventDefault(); go(-1); }
  };

  const markAllPresent = () =>
    setRows((rs) => rs.map((r) => (r.ispresent ? r : { ...r, ispresent: true, obtained_marks: "", isUpdate: true })));

  /* ---------------- stats ---------------- */
  const stats = useMemo(() => {
    const st = rows.map(rowState);
    const scored = st.filter((s) => s.status === "pass" || s.status === "fail").map((s) => s.marks);
    return {
      total: rows.length,
      entered: st.filter((s) => s.status !== "pending").length,
      absent: st.filter((s) => s.status === "absent").length,
      pass: st.filter((s) => s.status === "pass").length,
      fail: st.filter((s) => s.status === "fail").length,
      invalid: st.filter((s) => s.status === "invalid").length,
      highest: scored.length ? Math.max(...scored) : null,
      lowest: scored.length ? Math.min(...scored) : null,
      average: scored.length ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10 : null,
    };
    // eslint-disable-next-line
  }, [rows, maxMarks, minMarks, grades]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.student_name || "").toLowerCase().includes(q) || String(r.roll_no || "").includes(q));
  }, [rows, search]);

  /* ---------------- save / delete ---------------- */
  const save = async () => {
    if (stats.invalid) return toast.error(`Marks cannot be more than ${maxMarks}.`);
    const missing = rows.filter((r) => r.ispresent && isBlank(isGradeType ? r.obtained_grade : r.obtained_marks));
    if (missing.length) {
      toast.error(`Enter ${isGradeType ? "grade" : "marks"} for all present students (${missing.length} missing) or mark them absent.`);
      const el = inputs.current[missing[0].student_admissionid];
      if (el) el.focus();
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map((r) => (isGradeType
        ? { ...r, obtained_marks: "0", obtained_grade: r.ispresent ? r.obtained_grade : null }
        : { ...r, obtained_marks: r.ispresent ? String(Number(r.obtained_marks)) : "0", obtained_grade: null }));
      const res = await schoolApi.updateResult(payload);
      toast.success(res?.message || "Result saved");
      await loadStudents();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save result");
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async () => {
    try {
      const result = await schoolApi.deleteSchedule(record.id);
      if (result.success) {
        toast.success("Record deleted successfully");
        setTimeout(() => navigate(`/examschedule`), 1000);
      } else {
        toast.error(result.message);
        setModalShow(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
      setModalShow(false);
    }
  };

  /* ---------------- UI ---------------- */
  const statusBadge = (s) => {
    switch (s.status) {
      case "pass": return <Badge bg="success">Pass</Badge>;
      case "graded": return <Badge bg="success">Done</Badge>;
      case "fail": return <Badge bg="danger">Fail</Badge>;
      case "absent": return <Badge bg="secondary">Absent</Badge>;
      case "invalid": return <Badge bg="warning" text="dark">&gt; Max</Badge>;
      default: return <Badge bg="light" text="dark" className="border">Pending</Badge>;
    }
  };

  const Info = ({ icon, label, children }) => (
    <Col xs={6} md={4} lg={3} className="mb-3">
      <div className="text-muted small"><i className={`fa ${icon} me-1`}></i>{label}</div>
      <div className="fw-semibold">{children || "—"}</div>
    </Col>
  );

  const Stat = ({ label, value, variant = "dark" }) => (
    <Col xs={4} md={2} className="text-center mb-2">
      <div className={`fs-5 fw-bold text-${variant}`}>{value ?? "—"}</div>
      <div className="small text-muted">{label}</div>
    </Col>
  );

  const pct = stats.total ? Math.round((stats.entered * 100) / stats.total) : 0;

  return (
    <>
      <ToastContainer position="top-right" autoClose={2000} theme="light" />
      <Helmet><title>{props?.tabName || "Exam Details"}</title></Helmet>

      {modalShow && (
        <Confirm
          show={modalShow}
          onHide={() => setModalShow(false)}
          deleteSchedule={deleteSchedule}
          title="Confirm delete?"
          message="You are going to delete the record. Are you sure?"
          table="exam_schedule"
        />
      )}

      <Container fluid className="px-lg-4">
        <PageNavigations listName="Exam Details" listPath="/examschedule" colClassName="d-flex px-1 py-2" />

        {record && (
          <Card className="shadow-sm mb-3">
            <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-1">
                  {record.subject_name}
                  {record.exam_type_name && (
                    <Badge bg={isGradeType ? "warning" : "primary"} text={isGradeType ? "dark" : undefined} className="ms-2 align-middle">
                      {record.exam_type_name}
                    </Badge>
                  )}
                </h5>
                <div className="text-muted small">
                  {record.exam_title_name}
                  {record.exam_group_name && <> · <Badge bg="light" text="dark" className="border">{record.exam_group_name}</Badge></>}
                  {" · "}Class {record.class_name} [{record.section_name}]
                </div>
              </div>
              <div className="d-flex gap-2">
                {canEdit && (
                  <Button size="sm" className="edit-button" disabled={!isCurrentSession}
                    onClick={() => navigate(`/examschedule/${scheduleId}/e`, { state: record })}>
                    <i className="fa-regular fa-pen-to-square me-1"></i>Edit
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="outline-danger" disabled={!isCurrentSession} onClick={() => setModalShow(true)}>
                    <i className="fa fa-trash me-1"></i>Delete
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => navigate("/examschedule")}>Back</Button>
              </div>
            </Card.Header>
            <Card.Body className="pb-1">
              <Row>
                <Info icon="fa-calendar" label="Date">{record.schedule_date ? moment(record.schedule_date).format("DD MMM YYYY, ddd") : "–"}</Info>
                <Info icon="fa-clock" label="Time">{record.start_time ? <>{to12h(record.start_time)} – {to12h(record.end_time)}{record.duration ? ` (${record.duration})` : ""}</> : "–"}</Info>
                <Info icon="fa-door-open" label="Room">{record.room_no}</Info>
                <Info icon="fa-user-tie" label="Examiner">{record.examinor_info}</Info>
                <Info icon="fa-bullseye" label="Max / Pass Marks">
                  {isGradeType ? <span>Grade only</span> : <><span>{record.max_marks}</span> / <span className="text-danger">{record.min_marks}</span></>}
                </Info>
                <Info icon="fa-circle-check" label="Status">
                  <Badge bg={String(record.status).toLowerCase() === "active" ? "success" : "secondary"}>{record.status}</Badge>
                </Info>
                <Info icon="fa-star" label="Final Result Exam">{record.exam_title_isfinal ? "Yes" : "No"}</Info>
                <Info icon="fa-file-lines" label="Report Card">
                  <Link to="/reportcard/publish" className="text-decoration-none">Preview &amp; Publish →</Link>
                </Info>
              </Row>
            </Card.Body>
          </Card>
        )}

        {record && (
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h6 className="mb-0"><i className="fa fa-pen-to-square me-2"></i>{isGradeType ? "Grade Entry" : "Marks Entry"}</h6>
                <datalist id="grade-options">
                  {grades.map((g) => <option key={g.id || g.grade} value={g.grade} />)}
                </datalist>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <InputGroup size="sm" style={{ width: 220 }}>
                    <InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
                    <Form.Control placeholder="Search name / roll" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>
                  {canEnter && stats.absent > 0 && (
                    <Button size="sm" variant="outline-secondary" onClick={markAllPresent}>Mark all present</Button>
                  )}
                  {canEnter && (
                    <Button size="sm" className="add-new-button" disabled={saving || !dirty} onClick={save}>
                      {saving ? "Saving…" : "Save Result"}
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {examNotHeld && (
                <div className="alert alert-info py-2 small mb-3">
                  <i className="fa fa-circle-info me-1"></i>Marks can be entered on or after the exam date ({moment(record.schedule_date).format("DD-MM-YYYY")}).
                </div>
              )}
              {!isCurrentSession && (
                <div className="alert alert-warning py-2 small mb-3">You are viewing a past session – marks are read only.</div>
              )}

              {/* progress + statistics */}
              <div className="mb-3">
                <div className="d-flex justify-content-between small mb-1">
                  <span>Entered {stats.entered} of {stats.total} students</span>
                  {dirty && <span className="text-warning fw-semibold"><i className="fa fa-circle me-1" style={{ fontSize: 8 }}></i>Unsaved changes</span>}
                </div>
                <ProgressBar now={pct} variant={pct === 100 ? "success" : "primary"} style={{ height: 6 }} />
              </div>
              <Row className="border rounded mx-0 py-2 mb-3 bg-light">
                <Stat label="Students" value={stats.total} />
                <Stat label="Pass" value={stats.pass} variant="success" />
                <Stat label="Fail" value={stats.fail} variant="danger" />
                <Stat label="Absent" value={stats.absent} variant="secondary" />
                <Stat label="Average" value={stats.average} variant="primary" />
                <Stat label="Highest / Lowest" value={stats.highest == null ? null : `${stats.highest} / ${stats.lowest}`} />
              </Row>

              <div style={{ overflowX: "auto" }}>
                <Table hover size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 60 }}>Roll</th>
                      <th>Student</th>
                      <th style={{ width: 110 }} className="text-center">Present</th>
                      <th style={{ width: 170 }}>Marks <span className="text-muted fw-normal">/ {maxMarks}</span></th>
                      <th style={{ width: 80 }} className="text-center">%</th>
                      <th style={{ width: 80 }} className="text-center">Grade</th>
                      <th style={{ width: 90 }} className="text-center">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, i) => {
                      const s = rowState(r);
                      const bg = s.status === "fail" ? "table-danger" : s.status === "absent" ? "table-secondary" : s.status === "invalid" ? "table-warning" : "";
                      return (
                        <tr key={r.student_admissionid} className={bg}>
                          <td>{r.roll_no || ""}</td>
                          <td>
                            <div className="fw-semibold">{r.student_name}</div>
                            {r.sr_no && <small className="text-muted">S.R. {r.sr_no}</small>}
                            {r.isUpdate && <Badge bg="warning" text="dark" className="ms-2">edited</Badge>}
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              id={`present_${r.student_admissionid}`}
                              className="d-inline-block"
                              checked={r.ispresent === true}
                              disabled={!canEnter}
                              onChange={(e) => onPresent(r, e.target.checked)}
                              label={r.ispresent ? "" : "AB"}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              ref={(el) => { inputs.current[r.student_admissionid] = el; }}
                              inputMode={isGradeType ? "text" : "numeric"}
                              list={isGradeType ? "grade-options" : undefined}
                              value={r.ispresent ? (isGradeType ? r.obtained_grade : r.obtained_marks) : ""}
                              placeholder={r.ispresent ? (isGradeType ? "Enter grade" : "Enter marks") : "Absent"}
                              disabled={!canEnter || !r.ispresent}
                              isInvalid={s.status === "invalid"}
                              onChange={(e) => onMarks(r, e.target.value)}
                              onKeyDown={(e) => onKey(e, i)}
                              onFocus={(e) => e.target.select()}
                              style={{ maxWidth: 140 }}
                            />
                          </td>
                          <td className="text-center">{s.pct != null ? `${s.pct}%` : ""}</td>
                          <td className="text-center fw-bold">{s.grade || ""}</td>
                          <td className="text-center">{statusBadge(s)}</td>
                        </tr>
                      );
                    })}
                    {loaded && filtered.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted py-4">
                        {rows.length ? "No student matches your search" : "No active students found in this class / section"}
                      </td></tr>
                    )}
                    {!loaded && <tr><td colSpan={7} className="text-center text-muted py-4">Loading students…</td></tr>}
                  </tbody>
                </Table>
              </div>
              {canEnter && rows.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted"><i className="fa fa-keyboard me-1"></i>Press Enter / ↓ to move to the next student.</small>
                  <Button size="sm" className="add-new-button" disabled={saving || !dirty} onClick={save}>
                    {saving ? "Saving…" : "Save Result"}
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </>
  );
};

export default ExamScheduleView;
